<script setup lang="ts">
import { ref, watch, watchEffect, onUnmounted } from 'vue'

import chicaneToursImg from '@/assets/images/landingpageoverlay/Chicane Tours.png'
import dansLesVosgesImg from '@/assets/images/landingpageoverlay/Dans Les Vosges.png'
import dropFilesImg from '@/assets/images/landingpageoverlay/drop-files.png'
import globalMapImg from '@/assets/images/landingpageoverlay/global-map.png'
import hauteRouteImg from '@/assets/images/landingpageoverlay/Haute-Route.jpeg'
import richCollectionsImg from '@/assets/images/landingpageoverlay/rich collections.png'
import shareImg from '@/assets/images/landingpageoverlay/share-collaborate.png'
import SiteFooter from '@/components/static/SiteFooter.vue'
import { useEventListener } from '@/composables/useEventListener'
import { useSafeAreaHeaderClearance } from '@/services/safeAreaService'
import { getMixedImportFilePickerAccept } from '@/utils/filePickerAccept'
import { Z_INDEX } from '@/utils/zIndex'

interface Props {
  isOpen: boolean
}

interface Emits {
  close: []
  'explore-map': []
  'view-demo-collection': []
  'request-invite': []
  'drop-files': [files: FileList]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const handleStaticSite = (pageId: string) => {
  window.dispatchEvent(new CustomEvent('open-static-site', { detail: pageId }))
}

const isAnimatingOut = ref(false)
const isDragOver = ref(false)
const emailInput = ref('')
const emailSubmitted = ref(false)

const scrollEl = ref<HTMLElement | null>(null)
const overscrollY = ref(0)
// Threshold = 1 viewport height in px, captured on each open
const overscrollThreshold = ref(window.innerHeight)
const headerClearance = useSafeAreaHeaderClearance(48)

const closeWith = (afterClose: () => void) => {
  if (isAnimatingOut.value) return
  isAnimatingOut.value = true
  setTimeout(() => {
    isAnimatingOut.value = false
    overscrollY.value = 0
    afterClose()
  }, 450)
}

const handleClose = () => {
  closeWith(() => emit('close'))
}

const handleExploreMap = () => {
  closeWith(() => emit('explore-map'))
}

const handleViewDemoCollection = () => {
  closeWith(() => emit('view-demo-collection'))
}

const handleRequestInvite = () => {
  handleClose()
  // Emit after animation starts so the modal opens after overlay begins dismissing
  setTimeout(() => {
    emit('request-invite')
  }, 200)
}

const handleEmailSubmit = () => {
  emailSubmitted.value = true
}

const fileInputRef = ref<HTMLInputElement | null>(null)
const mixedImportFilePickerAccept = getMixedImportFilePickerAccept()

const handleDropZoneTap = () => {
  fileInputRef.value?.click()
}

const handleFileInputChange = (e: Event) => {
  const files = (e.target as HTMLInputElement).files
  if (files && files.length > 0) {
    emit('drop-files', files)
    handleClose()
  }
  // Reset so the same file can be selected again
  if (fileInputRef.value) fileInputRef.value.value = ''
}

// Drag & drop handlers for opaque overlay areas
const handleDragEnter = (event: DragEvent) => {
  event.preventDefault()
  if (event.dataTransfer?.types.includes('application/x-tour-reorder')) return
  isDragOver.value = true
}

const handleDragOver = (event: DragEvent) => {
  event.preventDefault()
  if (event.dataTransfer?.types.includes('application/x-tour-reorder')) return
  isDragOver.value = true
}

const handleDragLeave = (event: DragEvent) => {
  event.preventDefault()
  if (event.dataTransfer?.types.includes('application/x-tour-reorder')) return
  if (event.relatedTarget === null) {
    isDragOver.value = false
  }
}

const handleDrop = (event: DragEvent) => {
  event.preventDefault()
  if (event.dataTransfer?.types.includes('application/x-tour-reorder')) return
  isDragOver.value = false

  const files = event.dataTransfer?.files
  if (files && files.length > 0) {
    emit('drop-files', files)
    handleClose()
  }
}

// Track drags over the transparent map window via document-level events.
// pointer-events:none on the map window means those drag events bypass the overlay element,
// so we detect them at the document level to keep the isDragOver indicator accurate.
useEventListener(document, 'dragover', (event: Event) => {
  if (!props.isOpen) return
  const e = event as DragEvent
  if (e.dataTransfer?.types.includes('application/x-tour-reorder')) return
  if (e.dataTransfer?.types.includes('Files')) {
    isDragOver.value = true
  }
})

useEventListener(document, 'dragleave', (event: Event) => {
  if (!props.isOpen) return
  const e = event as DragEvent
  if (e.relatedTarget === null) {
    isDragOver.value = false
  }
})

useEventListener(document, 'drop', () => {
  if (!props.isOpen) return
  isDragOver.value = false
})

// Close when files are dropped directly on the map (transparent window area).
// WorkspacesView dispatches this event when its native drop handler receives files while
// the landing page is visible — the overlay itself never sees that drop because
// the map window has pointer-events:none.
useEventListener(window, 'close-landing-page', () => {
  if (!props.isOpen) return
  handleClose()
})

// Escape key to close
const handleKeyDown = (e: KeyboardEvent) => {
  if (!props.isOpen) return
  if (e.key === 'Escape') {
    e.preventDefault()
    handleClose()
  }
}

useEventListener(document, 'keydown', handleKeyDown)

// Overscroll-to-dismiss curtain: accumulated overscroll translates content upward until
// fully off-screen, then dismisses the overlay without a redundant fade animation.
const handleWheel = (e: WheelEvent) => {
  if (!props.isOpen || isAnimatingOut.value) return
  const el = scrollEl.value
  if (!el) return

  const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2

  if (atBottom && e.deltaY > 0) {
    e.preventDefault()
    overscrollY.value = Math.min(overscrollY.value + e.deltaY * 1.0, overscrollThreshold.value)
    if (overscrollY.value >= overscrollThreshold.value) {
      handleClose()
    }
  } else if (overscrollY.value > 0 && e.deltaY < 0) {
    e.preventDefault()
    overscrollY.value = Math.max(0, overscrollY.value + e.deltaY * 1.0)
  }
}

// Touch-based overscroll for mobile
const touchStartY = ref(0)
const touchLastY = ref(0)

const handleTouchStart = (e: TouchEvent) => {
  if (!props.isOpen || isAnimatingOut.value) return
  touchStartY.value = e.touches[0].clientY
  touchLastY.value = e.touches[0].clientY
}

const handleTouchMove = (e: TouchEvent) => {
  if (!props.isOpen || isAnimatingOut.value) return
  const el = scrollEl.value
  if (!el) return

  const currentY = e.touches[0].clientY
  const deltaY = touchLastY.value - currentY // positive = scrolling down
  touchLastY.value = currentY

  const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2

  if (atBottom && deltaY > 0) {
    e.preventDefault()
    overscrollY.value = Math.min(overscrollY.value + deltaY, overscrollThreshold.value)
    if (overscrollY.value >= overscrollThreshold.value) {
      handleClose()
    }
  } else if (overscrollY.value > 0 && deltaY < 0) {
    e.preventDefault()
    overscrollY.value = Math.max(0, overscrollY.value + deltaY)
  }
}

const handleTouchEnd = () => {
  // Leave curtain in place — no snap back; re-enable transition for wheel
  touchLastY.value = 0
}

watchEffect((onCleanup) => {
  const el = scrollEl.value
  if (!el) return
  el.addEventListener('wheel', handleWheel, { passive: false })
  el.addEventListener('touchstart', handleTouchStart, { passive: true })
  el.addEventListener('touchmove', handleTouchMove, { passive: false })
  el.addEventListener('touchend', handleTouchEnd, { passive: true })
  onCleanup(() => {
    el.removeEventListener('wheel', handleWheel)
    el.removeEventListener('touchstart', handleTouchStart)
    el.removeEventListener('touchmove', handleTouchMove)
    el.removeEventListener('touchend', handleTouchEnd)
  })
})

// Reset state when opening
watch(
  () => props.isOpen,
  (newVal) => {
    if (newVal) {
      isAnimatingOut.value = false
      isDragOver.value = false
      emailSubmitted.value = false
      emailInput.value = ''
      overscrollY.value = 0
      overscrollThreshold.value = window.innerHeight
      touchStartY.value = 0
      touchLastY.value = 0
    }
  },
)

onUnmounted(() => {
  isAnimatingOut.value = false
})
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 overflow-hidden"
      :style="{ zIndex: Z_INDEX.LANDING_OVERLAY }"
      role="dialog"
      aria-modal="true"
      aria-label="Welcome to route.camp"
    >
      <div
        ref="scrollEl"
        :class="[
          'h-full overflow-y-auto scrollable-container',
          isAnimatingOut ? 'landing-fade-out' : 'landing-fade-in',
        ]"
        :style="{ paddingTop: headerClearance }"
        @dragenter="handleDragEnter"
        @dragover="handleDragOver"
        @dragleave="handleDragLeave"
        @drop="handleDrop"
      >
        <!-- Curtain wrapper: only this content translates up on overscroll; footer is excluded -->
        <div
          :style="{
            transform: overscrollY > 0 ? `translateY(-${overscrollY}px)` : undefined,
            transition:
              touchLastY !== 0 ? 'none' : 'transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          }"
        >
          <!-- ===== HEADLINE + HERO + CTA wrapper: full viewport height minus header ===== -->
          <div class="h-[calc(100vh-12rem)] flex flex-col">
            <!-- HEADLINE (solid background) -->
            <section
              class="bg-[#FEFEFE] px-5 sm:px-8 pt-8 pb-5 sm:pt-16 sm:pb-6 border-b border-stone-200/60 shrink-0"
            >
              <div class="max-w-5xl mx-auto">
                <h1
                  class="font-mono text-3xl sm:text-4xl lg:text-5xl font-semibold text-stone-900 text-center leading-tight"
                >
                  All your adventures on one map
                </h1>
              </div>
            </section>

            <!-- HERO (transparent — map shows through), fills remaining height -->
            <section
              id="lp-hero"
              class="flex-1 flex flex-col items-center px-5 sm:px-8 cursor-default"
            >
              <!-- Hidden file input for mobile tap-to-upload -->
              <input
                ref="fileInputRef"
                type="file"
                multiple
                :accept="mixedImportFilePickerAccept"
                class="sr-only"
                aria-hidden="true"
                @change="handleFileInputChange"
              />
              <!-- Spacer: pushes drop zone into the lower third -->
              <div class="flex-[2]"></div>
              <div class="flex justify-center py-8 sm:py-10 w-full">
                <div
                  class="hint-drop-zone flex flex-col items-center gap-3 rounded-2xl px-8 py-6 transition-colors duration-200 cursor-pointer shadow-md"
                  :class="
                    isDragOver
                      ? 'bg-emerald-50 text-emerald-800 ring-2 ring-emerald-500'
                      : 'bg-white text-stone-800 ring-1 ring-stone-200/80'
                  "
                  role="button"
                  tabindex="0"
                  aria-label="Upload GPS files and photos"
                  @click="handleDropZoneTap"
                  @keydown.enter.space.prevent="handleDropZoneTap"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-7 h-7 shrink-0 transition-colors duration-200"
                    :class="isDragOver ? 'text-emerald-600' : 'text-stone-700'"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="1.5"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
                    />
                  </svg>
                  <p class="font-mono text-sm text-center leading-snug max-w-[22rem]">
                    Drop your GPX or FIT files and photos here.<br />No account needed.
                  </p>
                </div>
              </div>
              <div class="flex-1"></div>
            </section>

            <!-- Try CTA -->
            <div
              class="bg-[#FEFEFE] flex flex-wrap justify-center gap-3 pt-8 pb-6 sm:pt-8 sm:pb-6 shrink-0"
            >
              <button
                class="inline-flex items-center gap-2 px-7 py-3 rounded-lg bg-emerald-800 text-white text-sm font-medium hover:bg-emerald-700 transition-colors shadow-sm cursor-pointer"
                @click="handleExploreMap"
              >
                Explore the map
              </button>
              <button
                class="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-white text-stone-700 border border-stone-200 rounded-lg hover:border-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 transition-colors cursor-pointer"
                @click="handleViewDemoCollection"
              >
                View demo Collection
              </button>
            </div>
          </div>

          <!-- ===== SOLID SECTIONS (scroll up over the map) ===== -->
          <div class="bg-[#FEFEFE] relative">
            <!-- FEATURES -->
            <section id="lp-features" class="pt-16 pb-8 sm:pt-16 sm:pb-12 px-5 sm:px-8">
              <div class="max-w-5xl mx-auto">
                <span
                  class="block font-mono text-[11px] uppercase tracking-widest text-stone-500 text-center mb-2"
                >
                  Features
                </span>
                <h2
                  class="font-mono text-2xl sm:text-3xl font-semibold text-stone-900 text-center mb-14 sm:mb-20"
                >
                  A vivid archive of your trips and routes
                </h2>

                <div class="flex flex-col gap-16 sm:gap-20">
                  <!-- Feature 1 -->
                  <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                    <img
                      :src="globalMapImg"
                      alt="Global map view showing all tracks"
                      class="w-full aspect-[4/3] object-cover object-top rounded-xl border border-stone-100 shadow-sm"
                    />
                    <div>
                      <h3 class="font-mono text-base sm:text-lg font-semibold text-stone-900 mb-3">
                        One map to rule them all
                      </h3>
                      <p class="text-sm text-stone-700 leading-relaxed">
                        View all your past hikes, rides, expeditions, and planned routes on the
                        single map — color-coded, with powerful search and filters, interactive.
                      </p>
                    </div>
                  </div>

                  <!-- Feature 2 (mirrored) -->
                  <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                    <div class="lg:order-2">
                      <img
                        :src="richCollectionsImg"
                        alt="Rich collections with tracks and photos"
                        class="w-full aspect-[4/3] object-cover object-top rounded-xl border border-stone-100 shadow-sm"
                      />
                    </div>
                    <div class="lg:order-1">
                      <h3 class="font-mono text-base sm:text-lg font-semibold text-stone-900 mb-3">
                        Document trips and curate routes with Collections
                      </h3>
                      <p class="text-sm text-stone-700 leading-relaxed">
                        Collections can contain multiple tracks, points, and photos. Document a
                        multi-week adventure, plan alternates and highlights, or archive your club
                        routes with shareable Collections.
                      </p>
                    </div>
                  </div>

                  <!-- Feature 3 -->
                  <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                    <img
                      :src="shareImg"
                      alt="Share and collaborate on trips"
                      class="w-full aspect-[4/3] object-cover object-top rounded-xl border border-stone-100 shadow-sm"
                    />
                    <div>
                      <h3 class="font-mono text-base sm:text-lg font-semibold text-stone-900 mb-3">
                        Share trip updates and plan together
                      </h3>
                      <p class="text-sm text-stone-700 leading-relaxed">
                        Share your Collections while on the go — no account needed to view.
                        Collaborate on planning with other routecamp users.
                      </p>
                    </div>
                  </div>

                  <!-- Feature 4 (mirrored) -->
                  <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                    <div class="lg:order-2">
                      <img
                        :src="dropFilesImg"
                        alt="Drop files to import tracks"
                        class="w-full aspect-[4/3] object-cover object-top rounded-xl border border-stone-100 shadow-sm"
                      />
                    </div>
                    <div class="lg:order-1">
                      <h3 class="font-mono text-base sm:text-lg font-semibold text-stone-900 mb-3">
                        Fast import, faster export
                      </h3>
                      <p class="text-sm text-stone-700 leading-relaxed">
                        Visualize an entire odyssey in seconds by dropping the files onto the map.
                        Export your entire archive in one click — your files stay yours.
                      </p>
                    </div>
                  </div>
                </div>

                <!-- CTA below features -->
                <div class="text-center mt-16 sm:mt-20">
                  <button
                    class="inline-flex items-center gap-2 px-7 py-3 rounded-lg bg-emerald-800 text-white text-sm font-medium hover:bg-emerald-700 transition-colors shadow-sm cursor-pointer"
                    @click="handleExploreMap"
                  >
                    Map your trip — no account needed
                  </button>
                </div>
              </div>
            </section>

            <!-- USAGE / COMMUNITY -->
            <section id="lp-usage" class="py-16 sm:py-24 px-5 sm:px-8">
              <div class="max-w-5xl mx-auto">
                <span
                  class="block font-mono text-[11px] uppercase tracking-widest text-stone-500 text-center mb-2"
                >
                  What you can do
                </span>
                <h2
                  class="font-mono text-2xl sm:text-3xl font-semibold text-stone-900 text-center mb-14 sm:mb-16"
                >
                  Plan, Document, Curate
                </h2>

                <div class="grid grid-cols-1 sm:grid-cols-3 gap-8">
                  <div class="flex flex-col">
                    <h3 class="font-mono text-sm font-semibold text-stone-900 mb-2">
                      Plan together
                    </h3>
                    <p class="text-sm text-stone-700 leading-relaxed mb-5">
                      Get the group's input on the route so everyone's on the same page on the
                      trail.
                    </p>
                    <a
                      class="mt-auto"
                      href="https://route.camp/collections/1771877449378-5v2c28g4"
                      target="_blank"
                      rel="noopener"
                    >
                      <img
                        :src="dansLesVosgesImg"
                        alt="Dans Les Vosges collection on routecamp"
                        class="block w-full aspect-square object-cover object-top rounded-xl border border-stone-100 transition-opacity hover:opacity-85"
                      />
                    </a>
                  </div>
                  <div class="flex flex-col">
                    <h3 class="font-mono text-sm font-semibold text-stone-900 mb-2">
                      Document while on the go
                    </h3>
                    <p class="text-sm text-stone-700 leading-relaxed mb-5">
                      Add highlights of your trip from your phone so folks back home can follow
                      along.
                    </p>
                    <a
                      class="mt-auto"
                      href="https://route.camp/collections/1776016325484-x6wrx9cm"
                      target="_blank"
                      rel="noopener"
                    >
                      <img
                        :src="hauteRouteImg"
                        alt="Haute-Route collection on routecamp"
                        class="block w-full aspect-square object-cover object-top rounded-xl border border-stone-100 transition-opacity hover:opacity-85"
                      />
                    </a>
                  </div>
                  <div class="flex flex-col">
                    <h3 class="font-mono text-sm font-semibold text-stone-900 mb-2">
                      Curate for the community
                    </h3>
                    <p class="text-sm text-stone-700 leading-relaxed mb-5">
                      Publish a Collection of your favorite routes for your friends, club, or
                      anyone.
                    </p>
                    <a
                      class="mt-auto"
                      href="https://route.camp/collections/1754040857917-r8e2i8ep"
                      target="_blank"
                      rel="noopener"
                    >
                      <img
                        :src="chicaneToursImg"
                        alt="Chicane Tours collection on routecamp"
                        class="block w-full aspect-square object-cover object-top rounded-xl border border-stone-100 transition-opacity hover:opacity-85"
                      />
                    </a>
                  </div>
                </div>
              </div>
            </section>

            <!-- NEWSLETTER -->
            <section id="lp-newsletter" class="pt-16 pb-14 sm:pt-24 sm:pb-20 px-5 sm:px-8">
              <div class="max-w-3xl mx-auto text-center">
                <span
                  class="block font-mono text-[11px] uppercase tracking-widest text-stone-500 mb-3"
                >
                  Stay in the loop
                </span>
                <h2 class="font-mono text-2xl sm:text-3xl font-semibold text-stone-900 mb-4">
                  Join the journey
                </h2>
                <p class="text-sm text-stone-700 leading-relaxed">
                  We're outdoor enthusiasts building the map we've been missing.
                  <br />
                  routecamp is free for testing purposes — expect rough edges for now.
                  <br />
                  We're seeking paying members to support this independent, ad-free tool.<br />
                  <br />
                  Sign up for news and updates below.
                </p>

                <form
                  v-if="!emailSubmitted"
                  class="flex gap-2 flex-wrap justify-center mt-6"
                  @submit.prevent="handleEmailSubmit"
                >
                  <input
                    v-model="emailInput"
                    type="email"
                    required
                    placeholder="your@email.com"
                    autocomplete="email"
                    class="flex-1 min-w-0 px-4 py-2.5 text-sm border border-stone-200 rounded-lg bg-white text-stone-900 outline-none focus:border-emerald-700 transition-colors"
                  />
                  <button
                    type="submit"
                    class="px-5 py-2.5 text-sm font-medium bg-emerald-800 text-white rounded-lg hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Notify me
                  </button>
                </form>
                <p v-else class="text-sm text-emerald-700 font-medium mt-6">
                  You're on the list — we'll be in touch.
                </p>
              </div>
            </section>
          </div>

          <!-- ===== TRANSPARENT WINDOW + FINAL CTA (map shows through) ===== -->
          <section class="h-[calc(100vh-32rem)] flex items-center justify-center px-5">
            <div class="flex flex-wrap justify-center items-center gap-3">
              <button
                class="inline-flex items-center gap-2 px-7 py-3 rounded-lg bg-emerald-800 text-white text-sm font-medium hover:bg-emerald-700 transition-colors shadow-sm cursor-pointer"
                @click="handleExploreMap"
              >
                Try routecamp — no account needed
              </button>
              <button
                class="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-white text-stone-700 border border-stone-200 rounded-lg hover:border-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 transition-colors cursor-pointer"
                @click="handleRequestInvite"
              >
                Learn more about routecamp and the team
              </button>
            </div>
          </section>
        </div>
        <!-- end curtain wrapper -->

        <!-- FOOTER -->
        <SiteFooter @open-static-site="handleStaticSite" />
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
/* No background on the root overlay — the hero area is fully transparent so the real
   interactive map shows through. Solid sections below have their own BG. */

/* Fade-in on open */
.landing-fade-in {
  animation: landingFadeIn 0.35s ease-out both;
}

@keyframes landingFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Fade-down on close */
.landing-fade-out {
  animation: landingFadeDown 0.35s ease-in both;
}

@keyframes landingFadeDown {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(24px);
  }
}

/* Drop zone — periodic bounce + hover bounce */
.hint-drop-zone {
  animation: dropZoneIdle 5s ease-in-out infinite;
  will-change: transform;
}
.hint-drop-zone:hover {
  animation: dropZoneHover 0.45s ease-out forwards;
}

@keyframes dropZoneIdle {
  0% {
    transform: translateY(0);
  }
  5% {
    transform: translateY(-7px) scale(1.015);
  }
  10% {
    transform: translateY(0);
  }
  14% {
    transform: translateY(-4px);
  }
  18% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(0);
  }
}

@keyframes dropZoneHover {
  0% {
    transform: translateY(0);
  }
  28% {
    transform: translateY(-9px) scale(1.02);
  }
  55% {
    transform: translateY(0);
  }
  78% {
    transform: translateY(-5px);
  }
  100% {
    transform: translateY(0);
  }
}

/* Keep the map window fully transparent — nothing painted here */
.map-window {
  background: transparent !important;
}
</style>
